import React, { useState, useEffect, useCallback } from 'react';
import {
  Bug, Lightbulb, Sparkles, Plus, X, GripVertical,
  AlertCircle, ArrowUp, ArrowRight, Flame, ChevronDown, ChevronUp,
  Clock, Loader2, Trash2, CheckCircle2, Ban, MessageSquarePlus,
  CircleDot, ExternalLink,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axiosInstance from '../utils/axiosInstance';
import config from '../config';

const TYPE_CONFIG = {
  bug:         { label: 'Bug',         icon: Bug,       color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', dot: 'bg-red-500' },
  feature:     { label: 'Feature',     icon: Lightbulb, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', dot: 'bg-blue-500' },
  improvement: { label: 'Improvement', icon: Sparkles,  color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400', dot: 'bg-violet-500' },
};

const PRIORITY_CONFIG = {
  low:      { label: 'Low',      icon: ArrowRight, color: 'text-muted-foreground', bg: '' },
  medium:   { label: 'Medium',   icon: ArrowUp,    color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-900/10' },
  high:     { label: 'High',     icon: ArrowUp,    color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/10' },
  critical: { label: 'Critical', icon: Flame,      color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/10' },
};

const STATUS_COLUMNS = [
  { key: 'open',        label: 'Open',        icon: AlertCircle,  headerColor: 'border-t-blue-500' },
  { key: 'in_progress', label: 'In Progress', icon: CircleDot,     headerColor: 'border-t-amber-500' },
  { key: 'done',        label: 'Done',        icon: CheckCircle2, headerColor: 'border-t-emerald-500' },
  { key: 'wont_fix',    label: "Won't Fix",   icon: Ban,          headerColor: 'border-t-gray-400' },
];

const BugsFeatures = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', type: 'feature', priority: 'medium' });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all'); // all, bug, feature, improvement
  const [expandedId, setExpandedId] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/feature-requests/');
      setItems(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;
    setSaving(true);
    try {
      await axiosInstance.post('/api/feature-requests/', formData);
      setFormData({ title: '', description: '', type: 'feature', priority: 'medium' });
      setShowForm(false);
      fetchItems();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await axiosInstance.put(`/api/feature-requests/${id}/`, { status });
      setItems(prev => prev.map(i => i.id === id ? { ...i, status } : i));
    } catch { /* ignore */ }
  };

  const updatePriority = async (id, priority) => {
    try {
      await axiosInstance.put(`/api/feature-requests/${id}/`, { priority });
      setItems(prev => prev.map(i => i.id === id ? { ...i, priority } : i));
    } catch { /* ignore */ }
  };

  const deleteItem = async (id) => {
    if (!confirm('Delete this item?')) return;
    try {
      await axiosInstance.delete(`/api/feature-requests/${id}/`);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch { /* ignore */ }
  };

  const filteredItems = filter === 'all' ? items : items.filter(i => i.type === filter);

  const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
  const sortedByPriority = (list) => [...list].sort((a, b) => (priorityOrder[a.priority] ?? 9) - (priorityOrder[b.priority] ?? 9));

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className="px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <MessageSquarePlus className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Bugs & Features</h1>
            <p className="text-sm text-muted-foreground">
              Log issues, request features, track what's coming next.
            </p>
          </div>
        </div>
        <Button onClick={() => setShowForm(true)} disabled={showForm}>
          <Plus className="h-4 w-4 mr-1.5" /> New Request
        </Button>
      </div>

      {/* New request form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 rounded-xl border border-primary/30 bg-primary/5 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-foreground">New Request</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
            <Input
              placeholder="Title — e.g. 'Add CSV export to benchmark' or 'Pallet cost not showing for Norway'"
              value={formData.title}
              onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
              autoFocus
            />
            <select
              value={formData.type}
              onChange={(e) => setFormData(p => ({ ...p, type: e.target.value }))}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="bug">Bug</option>
              <option value="feature">Feature</option>
              <option value="improvement">Improvement</option>
            </select>
            <select
              value={formData.priority}
              onChange={(e) => setFormData(p => ({ ...p, priority: e.target.value }))}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <textarea
            placeholder="Description (optional) — any extra details, steps to reproduce, screenshots links..."
            value={formData.description}
            onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
            rows={2}
            className="mt-3 w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
          />
          <div className="mt-3 flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" size="sm" disabled={saving || !formData.title.trim()}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              Submit
            </Button>
          </div>
        </form>
      )}

      {/* Type filter pills */}
      <div className="flex items-center gap-2 mb-5">
        {[
          { key: 'all', label: 'All', count: items.length },
          { key: 'bug', label: 'Bugs', count: items.filter(i => i.type === 'bug').length },
          { key: 'feature', label: 'Features', count: items.filter(i => i.type === 'feature').length },
          { key: 'improvement', label: 'Improvements', count: items.filter(i => i.type === 'improvement').length },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              filter === f.key
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {f.label}
            <span className={`rounded-full px-1.5 py-0 text-[10px] ${filter === f.key ? 'bg-primary-foreground/20' : 'bg-background'}`}>
              {f.count}
            </span>
          </button>
        ))}
      </div>

      {/* Kanban columns */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
          {STATUS_COLUMNS.map(col => {
            const colItems = sortedByPriority(filteredItems.filter(i => i.status === col.key));
            const ColIcon = col.icon;
            return (
              <div key={col.key} className={`rounded-xl border border-border/70 surface-panel overflow-hidden border-t-4 ${col.headerColor}`}>
                {/* Column header */}
                <div className="px-4 py-3 border-b border-border bg-muted/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ColIcon className={`h-4 w-4 ${col.key === 'in_progress' ? 'text-amber-500' : ''} ${col.key === 'done' ? 'text-emerald-500' : col.key === 'wont_fix' ? 'text-gray-400' : 'text-foreground'}`} />
                      <span className="font-semibold text-sm">{col.label}</span>
                    </div>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      {colItems.length}
                    </span>
                  </div>
                </div>

                {/* Cards */}
                <div className="p-2 space-y-2 min-h-[120px]">
                  {colItems.length === 0 && (
                    <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
                      No items
                    </div>
                  )}
                  {colItems.map(item => {
                    const typeConf = TYPE_CONFIG[item.type] || TYPE_CONFIG.feature;
                    const prioConf = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.medium;
                    const TypeIcon = typeConf.icon;
                    const PrioIcon = prioConf.icon;
                    const isExpanded = expandedId === item.id;
                    // Link to Odoo picking by name
                    const odooUrl = item.reference && item.reference.match(/LAMP\/OUT\//)
                      ? `${config.odooBaseUrl}/odoo/inventory/delivery?search=${encodeURIComponent(item.reference)}`
                      : null;
                    return (
                      <div
                        key={item.id}
                        className={`group rounded-lg border border-border bg-background shadow-sm hover:shadow-md transition-shadow ${prioConf.bg} ${isExpanded ? 'ring-1 ring-primary/30' : ''}`}
                      >
                        {/* Clickable card header */}
                        <div
                          className="p-3 cursor-pointer"
                          onClick={() => setExpandedId(isExpanded ? null : item.id)}
                        >
                          {/* Top row: type badge + priority */}
                          <div className="flex items-center justify-between mb-1.5">
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${typeConf.color}`}>
                              <TypeIcon className="h-3 w-3" />
                              {typeConf.label}
                            </span>
                            <div className="flex items-center gap-1">
                              <select
                                value={item.priority}
                                onChange={(e) => { e.stopPropagation(); updatePriority(item.id, e.target.value); }}
                                onClick={(e) => e.stopPropagation()}
                                className={`appearance-none bg-transparent border-none text-[10px] font-semibold cursor-pointer pr-0 ${prioConf.color}`}
                                title={`Priority: ${prioConf.label}`}
                              >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="critical">Critical</option>
                              </select>
                              <PrioIcon className={`h-3 w-3 ${prioConf.color} ${item.priority === 'critical' ? 'animate-pulse' : ''}`} />
                            </div>
                          </div>

                          {/* Title */}
                          <p className="text-sm font-medium text-foreground leading-snug">{item.title}</p>

                          {/* Description preview (collapsed) */}
                          {!isExpanded && item.description && (
                            <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{item.description}</p>
                          )}

                          {/* Footer: author, reference, time */}
                          <div className="mt-2 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                              {item.author && <span>{item.author}</span>}
                              {!item.author && item.submitted_by && <span>{item.submitted_by}</span>}
                              {item.reference && <span className="font-mono bg-muted px-1 rounded">{item.reference}</span>}
                              <span className="flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" />{timeAgo(item.created_at)}</span>
                            </div>
                            {(item.description || item.reference) && (
                              isExpanded
                                ? <ChevronUp className="h-3 w-3 text-muted-foreground" />
                                : <ChevronDown className="h-3 w-3 text-muted-foreground" />
                            )}
                          </div>
                        </div>

                        {/* Expanded detail */}
                        {isExpanded && (
                          <div className="border-t border-border px-3 pb-3 pt-2">
                            {item.description && (
                              <p className="text-xs text-foreground whitespace-pre-wrap mb-2">{item.description}</p>
                            )}

                            {odooUrl && (
                              <a
                                href={odooUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline mb-2"
                              >
                                <ExternalLink className="h-3 w-3" /> Open in Odoo
                              </a>
                            )}

                            {/* Actions */}
                            <div className="flex items-center gap-1 mt-1">
                              {col.key !== 'in_progress' && col.key !== 'done' && (
                                <button
                                  onClick={() => updateStatus(item.id, 'in_progress')}
                                  className="rounded px-2 py-1 text-[10px] font-medium hover:bg-amber-100 dark:hover:bg-amber-900/30 text-amber-600 border border-amber-200 dark:border-amber-800"
                                >
                                  In Progress
                                </button>
                              )}
                              {col.key !== 'done' && (
                                <button
                                  onClick={() => updateStatus(item.id, 'done')}
                                  className="rounded px-2 py-1 text-[10px] font-medium hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-emerald-600 border border-emerald-200 dark:border-emerald-800"
                                >
                                  Done
                                </button>
                              )}
                              {col.key === 'done' && (
                                <button
                                  onClick={() => updateStatus(item.id, 'open')}
                                  className="rounded px-2 py-1 text-[10px] font-medium hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 border border-blue-200 dark:border-blue-800"
                                >
                                  Reopen
                                </button>
                              )}
                              <button
                                onClick={() => deleteItem(item.id)}
                                className="rounded px-2 py-1 text-[10px] font-medium hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 border border-red-200 dark:border-red-800 ml-auto"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Hover actions (collapsed only) */}
                        {!isExpanded && (
                          <div className="flex items-center gap-0.5 px-3 pb-2 opacity-0 group-hover:opacity-100 transition-opacity justify-end">
                            {col.key !== 'in_progress' && col.key !== 'done' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); updateStatus(item.id, 'in_progress'); }}
                                className="rounded p-1 hover:bg-amber-100 dark:hover:bg-amber-900/30 text-amber-600"
                                title="Move to In Progress"
                              >
                                <ArrowRight className="h-3 w-3" />
                              </button>
                            )}
                            {col.key !== 'done' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); updateStatus(item.id, 'done'); }}
                                className="rounded p-1 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-emerald-600"
                                title="Mark as Done"
                              >
                                <CheckCircle2 className="h-3 w-3" />
                              </button>
                            )}
                            {col.key === 'done' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); updateStatus(item.id, 'open'); }}
                                className="rounded p-1 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600"
                                title="Reopen"
                              >
                                <AlertCircle className="h-3 w-3" />
                              </button>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteItem(item.id); }}
                              className="rounded p-1 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500"
                              title="Delete"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BugsFeatures;
